import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  X,
  Package,
  Loader2,
  ChevronLeft,
  Tag,
  Star,
  ShoppingBag,
  History,
  Filter,
  FileText,
  Info,
  Phone,
  HelpCircle,
  Scale,
  Mail,
  User,
  Home,
  Heart,
  Shield,
  AlertCircle,
  FolderTree,
} from "lucide-react";
import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { productService } from "../../services/product.service";
import { categoryService } from "../../services/category.service";
import { debounce, replace } from "lodash";

const SearchOverlay = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [categories, setCategories] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("all"); // 'products', 'categories', 'pages', 'all'
  const [totalResults, setTotalResults] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [searchError, setSearchError] = useState(null);

  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  // Internal pages data for search
  const internalPages = [
    {
      title: "الرئيسية",
      path: "/",
      icon: Home,
      keywords: ["home", "main", "الرئيسية"],
    },
    {
      title: "من نحن",
      path: "/about",
      icon: Info,
      keywords: ["about", "من نحن", "عن المتجر"],
    },
    {
      title: "اتصل بنا",
      path: "/contact",
      icon: Phone,
      keywords: ["contact", "اتصل", "تواصل"],
    },
    {
      title: "المفضلة",
      path: "/wishlist",
      icon: Heart,
      keywords: ["wishlist", "المفضلة", "محفوظات"],
    },
    {
      title: "سلة التسوق",
      path: "/cart",
      icon: ShoppingBag,
      keywords: ["cart", "سلة", "عربة"],
    },
    {
      title: "الطلبات",
      path: "/orders",
      icon: Package,
      keywords: ["orders", "طلبات", "مشتريات"],
    },
    {
      title: "الأسئلة الشائعة",
      path: "/faqs",
      icon: HelpCircle,
      keywords: ["faq", "أسئلة", "مساعدة"],
    },
    {
      title: "سياسة الخصوصية",
      path: "/privacy",
      icon: Shield,
      keywords: ["privacy", "خصوصية", "سياسة"],
    },
    {
      title: "الشروط والأحكام",
      path: "/terms",
      icon: Scale,
      keywords: ["terms", "شروط", "أحكام"],
    },
    {
      title: "سياسة الإرجاع",
      path: "/returns",
      icon: AlertCircle,
      keywords: ["returns", "إرجاع", "استبدال"],
    },
    {
      title: "المدونة",
      path: "/blog",
      icon: FileText,
      keywords: ["blog", "مدونة", "مقالات"],
    },
    {
      title: "العروض",
      path: "/sales",
      icon: Tag,
      keywords: ["sales", "عروض", "تخفيضات"],
    },
    {
      title: "طلب بيع",
      path: "/request",
      icon: Mail,
      keywords: ["request", "طلب", "بيع"],
    },
  ];

  // Load recent searches from localStorage
  useEffect(() => {
    const saved = localStorage.getItem("recentSearches");
    if (saved) {
      setRecentSearches(JSON.parse(saved).slice(0, 5));
    }
  }, []);

  // Load categories for suggestions
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await categoryService.getActiveCategories();
        if (response.success && response.data) {
          setCategories(response.data.slice(0, 6));
        }
      } catch (error) {
        console.error("Error loading categories:", error);
      }
    };
    loadCategories();
  }, []);

  // Search internal pages
  const searchInternalPages = (query) => {
    if (!query.trim()) return [];

    const lowerQuery = query.toLowerCase().trim();
    return internalPages
      .filter(
        (page) =>
          page.title.includes(lowerQuery) ||
          page.keywords.some((keyword) => keyword.includes(lowerQuery)),
      )
      .map((page) => ({
        id: `page-${page.path}`,
        type: "page",
        title: page.title,
        path: page.path,
        icon: page.icon,
        description: `صفحة ${page.title}`,
      }));
  };

  // Debounced search function
  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query.trim()) {
        setSearchResults([]);
        setTotalResults(0);
        setSearchError(null);
        return;
      }

      setIsLoading(true);
      setSearchError(null);

      try {
        let results = [];
        let total = 0;
        let apiResponse = null;

        // Search internal pages if active tab is 'pages' or 'all'
        if (activeTab === "pages" || activeTab === "all") {
          const pageResults = searchInternalPages(query);
          results = [...results, ...pageResults];
          total += pageResults.length;
        }

        // Search products via API
        if (activeTab === "products" || activeTab === "all") {
          const searchParams = {
            limit: activeTab === "all" ? 5 : 10,
          };

          const response = await productService.searchProducts(
            query,
            searchParams,
          );
          apiResponse = response;

          if (response.success && response.data) {
            // Handle different response structures
            let apiResults = [];

            if (Array.isArray(response.data)) {
              apiResults = response.data;
            } else if (
              response.data.data &&
              Array.isArray(response.data.data)
            ) {
              apiResults = response.data.data;
            } else if (
              response.data.items &&
              Array.isArray(response.data.items)
            ) {
              apiResults = response.data.items;
            } else if (
              response.data.results &&
              Array.isArray(response.data.results)
            ) {
              apiResults = response.data.results;
            }

            // Mark as products
            const productResults = apiResults.map((item) => ({
              ...item,
              type: "product",
            }));

            results = [...results, ...productResults];
            total += productResults.length;
          }
        }

        // Search categories separately via categoryService
        if (activeTab === "categories" || activeTab === "all") {
          try {
            // Get all categories and filter by search query
            const categoriesResponse =
              await categoryService.getActiveCategories();

            if (categoriesResponse.success && categoriesResponse.data) {
              let allCategories = [];

              if (Array.isArray(categoriesResponse.data)) {
                allCategories = categoriesResponse.data;
              } else if (
                categoriesResponse.data.data &&
                Array.isArray(categoriesResponse.data.data)
              ) {
                allCategories = categoriesResponse.data.data;
              }

              // Filter categories by name matching the query
              const lowerQuery = query.toLowerCase();
              const matchedCategories = allCategories
                .filter((cat) => cat.name?.toLowerCase().includes(lowerQuery))
                .slice(0, activeTab === "all" ? 5 : 10)
                .map((cat) => ({
                  id: cat.id,
                  name: cat.name,
                  slug: cat.slug,
                  description: cat.description,
                  image: cat.image,
                  icon: cat.icon,
                  product_count: cat.product_count || 0,
                  type: "category",
                }));

              results = [...results, ...matchedCategories];
              total += matchedCategories.length;
            }
          } catch (error) {
            console.error("Error searching categories:", error);
          }
        }

        // Sort results by relevance
        const lowerQuery = query.toLowerCase();
        results.sort((a, b) => {
          const aName = (a.name || a.title || "").toLowerCase();
          const bName = (b.name || b.title || "").toLowerCase();

          // Exact matches first
          if (aName === lowerQuery && bName !== lowerQuery) return -1;
          if (bName === lowerQuery && aName !== lowerQuery) return 1;

          // Starts with query next
          const aStarts = aName.startsWith(lowerQuery);
          const bStarts = bName.startsWith(lowerQuery);
          if (aStarts && !bStarts) return -1;
          if (bStarts && !aStarts) return 1;

          // Contains query next
          const aContains = aName.includes(lowerQuery);
          const bContains = bName.includes(lowerQuery);
          if (aContains && !bContains) return -1;
          if (bContains && !aContains) return 1;

          return 0;
        });

        setSearchResults(results);
        setTotalResults(total);
        setSelectedIndex(-1);

        // Only set error if we got no results AND the API call failed
        if (
          results.length === 0 &&
          total === 0 &&
          apiResponse &&
          !apiResponse.success
        ) {
          setSearchError(apiResponse.message || "لا توجد نتائج للبحث");
        }
      } catch (error) {
        setSearchError(error.message || "حدث خطأ في البحث");
        setSearchResults([]);
        setTotalResults(0);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    [activeTab],
  );

  useEffect(() => {
    debouncedSearch(searchQuery);
    return () => debouncedSearch.cancel();
  }, [searchQuery, debouncedSearch]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      setTimeout(() => inputRef.current?.focus(), 100);
      setSelectedIndex(-1);
      setSearchError(null);
    } else {
      document.body.style.overflow = "unset";
      setSearchQuery("");
      setSearchResults([]);
      setActiveTab("all");
      setSearchError(null);
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };

    const handleKeyDown = (e) => {
      if (!isOpen) return;

      // Arrow navigation
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < searchResults.length - 1 ? prev + 1 : prev,
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
      } else if (e.key === "Enter" && selectedIndex >= 0) {
        e.preventDefault();
        handleSelectResult(searchResults[selectedIndex]);
      } else if (
        e.key === "Enter" &&
        searchQuery.trim() &&
        searchResults.length === 0
      ) {
        e.preventDefault();
        handleSearch(new Event("submit"));
      }
    };

    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose, searchResults, selectedIndex, searchQuery]);

  // Scroll selected item into view
  useEffect(() => {
    if (selectedIndex >= 0 && resultsRef.current) {
      const selectedElement = resultsRef.current.children[selectedIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth",
        });
      }
    }
  }, [selectedIndex]);

  const saveRecentSearch = (query) => {
    const updated = [query, ...recentSearches.filter((s) => s !== query)].slice(
      0,
      5,
    );
    setRecentSearches(updated);
    localStorage.setItem("recentSearches", JSON.stringify(updated));
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      saveRecentSearch(searchQuery);

      // Determine where to navigate based on active tab
      if (activeTab === "pages") {
        onClose();
      } else if (activeTab === "categories") {
        navigate(`/categories?search=${encodeURIComponent(searchQuery)}`);
        onClose();
      } else {
        navigate(`/products?search=${encodeURIComponent(searchQuery)}`);
        onClose();
      }
    }
  };

  const handleQuickSearch = (query) => {
    setSearchQuery(query);
    saveRecentSearch(query);
    navigate(`/products?search=${encodeURIComponent(query)}`);
    onClose();
  };

  const handleSelectResult = (result) => {
    saveRecentSearch(
      result.name || result.title || result.query || searchQuery,
    );

    if (result.type === "page") {
      navigate(result.path);
    } else if (result.type === "category") {
      const slug = result.slug || result.id;
      navigate(`/categories/${slug}`);
    } else {
      const slug = result.slug || result.id;
      navigate(`/products/${slug}`);
    }
    onClose();
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem("recentSearches");
  };

  // Animation variants
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { duration: 0.3, ease: "easeOut" },
    },
    exit: {
      opacity: 0,
      transition: { duration: 0.2, ease: "easeIn" },
    },
  };

  const contentVariants = {
    hidden: { scale: 0.95, opacity: 0, y: 20 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 30,
        delay: 0.1,
      },
    },
    exit: {
      scale: 0.95,
      opacity: 0,
      y: 20,
      transition: { duration: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0 },
    hover: {
      scale: 1.02,
      backgroundColor: "var(--bg-glass)",
      transition: { duration: 0.2 },
    },
  };

  // Get icon for result type
  const getResultIcon = (result) => {
    if (result.type === "page") {
      const Icon = result.icon || FileText;
      return (
        <Icon className="w-5 h-5" style={{ color: "var(--primary-500)" }} />
      );
    } else if (result.type === "category") {
      return (
        <FolderTree
          className="w-5 h-5"
          style={{ color: "var(--primary-500)" }}
        />
      );
    } else {
      return (
        <Package className="w-5 h-5" style={{ color: "var(--primary-500)" }} />
      );
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999]"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          style={{
            background: "var(--bg-overlay)",
            backdropFilter: "blur(12px)",
          }}
        >
          {/* Close Button */}
          <motion.button
            onClick={onClose}
            className="absolute top-6 left-6 z-10 group"
            whileHover={{ scale: 1.1, rotate: 90 }}
            whileTap={{ scale: 0.9 }}
            transition={{ duration: 0.2 }}
          >
            <div
              className="p-3 rounded-2xl relative overflow-hidden"
              style={{
                background: "var(--gradient-primary)",
                backdropFilter: "blur(12px)",
                border: "1px solid var(--border-glass)",
                boxShadow: "var(--shadow-lg)",
              }}
            >
              <X className="w-6 h-6" style={{ color: "white" }} />
            </div>
          </motion.button>

          {/* Main Content */}
          <motion.div
            className="h-full flex flex-col items-center justify-start pt-24 px-4 md:px-6"
            variants={contentVariants}
          >
            <div className="w-full max-w-3xl">
              {/* Search Form with Glass Effect */}
              <motion.form
                onSubmit={handleSearch}
                className="relative mb-10"
                initial={{ y: -20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: "var(--gradient-glass)",
                    filter: "blur(20px)",
                    opacity: 0.5,
                  }}
                />

                <div
                  className="relative flex items-center rounded-2xl border-2 transition-all duration-300"
                  style={{
                    background: "var(--bg-glass)",
                    backdropFilter: "blur(12px)",
                    borderColor: searchQuery
                      ? "var(--primary-500)"
                      : "var(--border-glass)",
                    boxShadow: searchQuery
                      ? "var(--shadow-glow)"
                      : "var(--shadow-lg)",
                  }}
                >
                  <Search
                    className="absolute right-5 w-5 h-5"
                    style={{
                      color: searchQuery
                        ? "var(--primary-500)"
                        : "var(--text-tertiary)",
                    }}
                  />

                  <input
                    ref={inputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="ابحث عن منتجات، فئات، صفحات..."
                    className="w-full p-5 pr-14 text-lg rounded-2xl bg-transparent outline-none"
                    style={{ color: "#FFF" }}
                  />

                  {searchQuery && (
                    <motion.button
                      type="button"
                      onClick={() => setSearchQuery("")}
                      className="absolute left-4 p-1 rounded-full"
                      style={{ background: "var(--bg-tertiary)" }}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <X
                        className="w-4 h-4"
                        style={{ color: "var(--text-white)" }}
                      />
                    </motion.button>
                  )}
                </div>

                {/* Search Stats */}
                {searchQuery && !isLoading && !searchError && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute -bottom-6 right-0 text-sm"
                    style={{ color: "var(--text-tertiary)" }}
                  >
                    {totalResults} {totalResults === 1 ? "نتيجة" : "نتائج"}
                  </motion.div>
                )}

                {/* Search Error */}
                {searchError && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute -bottom-6 right-0 text-sm"
                    style={{ color: "var(--error-500)" }}
                  >
                    {searchError}
                  </motion.div>
                )}
              </motion.form>

              {/* Tabs for different search types */}
              {searchQuery && (
                <motion.div
                  className="flex flex-wrap gap-2 mb-6"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  {[
                    { id: "all", label: "الكل", icon: Filter },
                    { id: "products", label: "منتجات", icon: Package },
                    { id: "categories", label: "فئات", icon: FolderTree },
                    { id: "pages", label: "صفحات", icon: FileText },
                  ].map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <motion.button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className="px-4 py-2 rounded-xl text-sm font-medium flex items-center gap-2 transition-all duration-200"
                        style={{
                          background:
                            activeTab === tab.id
                              ? "var(--gradient-primary)"
                              : "var(--bg-glass)",
                          color:
                            activeTab === tab.id
                              ? "white"
                              : "var(--text-primary)",
                          border: "1px solid var(--border-glass)",
                        }}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Icon className="w-4 h-4" />
                        {tab.label}
                      </motion.button>
                    );
                  })}
                </motion.div>
              )}

              {/* Loading State */}
              {isLoading && (
                <motion.div
                  className="flex justify-center py-12"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <div className="text-center">
                    <Loader2
                      className="w-10 h-10 animate-spin mx-auto mb-3"
                      style={{ color: "var(--primary-500)" }}
                    />
                    <p style={{ color: "var(--text-secondary)" }}>
                      جاري البحث...
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Search Results */}
              {!isLoading && searchResults.length > 0 && (
                <motion.div
                  ref={resultsRef}
                  className="space-y-2 max-h-[50vh] overflow-y-auto rounded-2xl p-2 scrollbar-transparent"
                  style={{
                    background: "var(--bg-glass)",
                    backdropFilter: "blur(12px)",
                    border: "1px solid var(--border-glass)",
                    scrollbarWidth: "thin",
                    scrollbarColor: "var(--primary-500) transparent",
                  }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  {searchResults.map((result, index) => (
                    <motion.div
                      key={result.id || `result-${index}`}
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      whileHover="hover"
                      custom={index}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 flex items-center gap-4 group ${
                        selectedIndex === index ? "ring-2" : ""
                      }`}
                      style={{
                        background:
                          selectedIndex === index
                            ? "var(--bg-glass)"
                            : "transparent",
                        borderColor:
                          selectedIndex === index
                            ? "var(--primary-500)"
                            : "transparent",
                      }}
                      onClick={() => handleSelectResult(result)}
                      onMouseEnter={() => setSelectedIndex(index)}
                    >
                      {/* Result Icon */}
                      <div
                        className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 flex items-center justify-center"
                        style={{ background: "var(--bg-secondary)" }}
                      >
                        {result.main_image ? (
                          <img
                            src={result.main_image.url}
                            alt={result.name}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                          />
                        ) : (
                          getResultIcon(result)
                        )}
                      </div>

                      {/* Result Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h4
                            className="font-bold truncate"
                            style={{
                              color:
                                selectedIndex === index
                                  ? "var(--primary-500)"
                                  : "#FFF",
                            }}
                          >
                            {result.name || result.title}
                          </h4>
                          {result.is_sale && (
                            <span className="px-2 py-0.5 text-xs rounded-full bg-primary-800/10 text-primary-500">
                              تخفيض
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-3 text-sm">
                          {/* Result Type Badge */}
                          <span className="flex items-center gap-1">
                            <span
                              className="text-xs px-2 py-0.5 rounded-full"
                              style={{
                                background:
                                  result.type === "page"
                                    ? "var(--info-500/10)"
                                    : result.type === "category"
                                      ? "var(--success-500/10)"
                                      : "var(--primary-500/10)",
                                color:
                                  result.type === "page"
                                    ? "var(--info-600)"
                                    : result.type === "category"
                                      ? "var(--success-600)"
                                      : "var(--primary-600)",
                              }}
                            >
                              {result.type === "page"
                                ? "صفحة"
                                : result.type === "category"
                                  ? "فئة"
                                  : "منتج"}
                            </span>
                          </span>

                          {result.category_name && (
                            <span className="flex items-center gap-1">
                              <FolderTree
                                className="w-3 h-3"
                                style={{ color: "var(--text-tertiary)" }}
                              />
                              <span style={{ color: "var(--text-secondary)" }}>
                                {result.category_name}
                              </span>
                            </span>
                          )}

                          {result.price && (
                            <span className="flex items-center gap-1">
                              <ShoppingBag
                                className="w-3 h-3"
                                style={{ color: "var(--text-tertiary)" }}
                              />
                              <span style={{ color: "var(--primary-500)" }}>
                                {result.price} ج.م
                              </span>
                            </span>
                          )}

                          {result.description && (
                            <span
                              className="text-xs truncate"
                              style={{ color: "var(--text-tertiary)" }}
                            >
                              {result.description}
                            </span>
                          )}
                        </div>

                        {/* Rating for products */}
                        {result.rating_avg > 0 && (
                          <div className="flex items-center gap-1 mt-1">
                            <div className="flex">
                              {[...Array(5)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`w-3 h-3 ${
                                    i < Math.floor(result.rating_avg)
                                      ? "text-amber-500 fill-current"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                            </div>
                            <span
                              className="text-xs"
                              style={{ color: "var(--text-tertiary)" }}
                            >
                              ({result.rating_count})
                            </span>
                          </div>
                        )}
                      </div>

                      {/* View Details Icon */}
                      <ChevronLeft
                        className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity"
                        style={{ color: "var(--primary-500)" }}
                      />
                    </motion.div>
                  ))}
                </motion.div>
              )}

              {/* No Results */}
              {!isLoading &&
                searchQuery &&
                searchResults.length === 0 &&
                !searchError && (
                  <motion.div
                    className="text-center py-12"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                  >
                    <div
                      className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4"
                      style={{ background: "var(--bg-glass)" }}
                    >
                      <Search
                        className="w-8 h-8"
                        style={{ color: "var(--text-tertiary)" }}
                      />
                    </div>
                    <h3
                      className="text-lg font-bold mb-2"
                      style={{ color: "white" }}
                    >
                      لا توجد نتائج
                    </h3>
                    <p
                      className="mb-4"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      جرب البحث بكلمات مختلفة أو تصفح الفئات
                    </p>
                    <motion.button
                      onClick={() => navigate("/products")}
                      className="px-6 py-3 rounded-xl font-medium"
                      style={{
                        background: "var(--gradient-primary)",
                        color: "white",
                      }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      تصفح جميع المنتجات
                    </motion.button>
                  </motion.div>
                )}

              {/* Recent Searches */}
              {!searchQuery && recentSearches.length > 0 && (
                <motion.div
                  className="mb-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                      <History
                        className="w-5 h-5"
                        style={{ color: "var(--primary-500)" }}
                      />
                      <h3 className="font-semibold" style={{ color: "white" }}>
                        عمليات البحث الأخيرة
                      </h3>
                    </div>
                    <motion.button
                      onClick={clearRecentSearches}
                      className="text-sm"
                      style={{ color: "var(--text-tertiary)" }}
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      مسح الكل
                    </motion.button>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {recentSearches.map((search, index) => (
                      <motion.button
                        key={search}
                        onClick={() => handleQuickSearch(search)}
                        className="px-4 py-2 rounded-xl text-sm transition-all duration-200 group"
                        style={{
                          background: "var(--bg-glass)",
                          border: "1px solid var(--border-glass)",
                        }}
                        whileHover={{ scale: 1.05, y: -2 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <span
                          className="group-hover:text-primary-500 transition-colors"
                          style={{ color: "#FFF" }}
                        >
                          {search}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}

              {/* Categories Quick Access */}
              {!searchQuery && categories.length > 0 && (
                <motion.div
                  className="mt-8"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <div className="flex items-center gap-2 mb-4">
                    <FolderTree
                      className="w-5 h-5"
                      style={{ color: "var(--primary-500)" }}
                    />
                    <h3 className="font-semibold" style={{ color: "#FFF" }}>
                      تصفح الفئات
                    </h3>
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {categories.map((category, index) => (
                      <motion.button
                        key={category.id}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation(); // Prevent event bubbling
                          // Close overlay first, then navigate with a tiny delay
                          onClose();
                          setTimeout(() => {
                            navigate(`/categories/${category.slug}`);
                          }, 50);
                        }}
                        className="p-4 rounded-xl text-right transition-all duration-200 group"
                        style={{
                          background: "var(--bg-glass)",
                          border: "1px solid var(--border-glass)",
                        }}
                        whileHover={{ scale: 1.02, y: -2 }}
                        whileTap={{ scale: 0.98 }}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.2 + index * 0.05 }}
                      >
                        <div className="flex items-center gap-3">
                          <FolderTree
                            className="w-8 h-8"
                            style={{ color: "var(--primary-500)" }}
                          />
                          <div>
                            <span
                              className="block text-sm font-medium group-hover:text-primary-500 transition-colors"
                              style={{ color: "#FFF" }}
                            >
                              {category.name}
                            </span>
                            <span
                              className="text-xs opacity-60 mt-1 block"
                              style={{ color: "#FFF" }}
                            >
                              {category.product_count || 0} منتج
                            </span>
                          </div>
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SearchOverlay;
